import MarkdownIt from "markdown-it";
import { parseHtmlDocument } from "./html-document-parser.js";
import { latexToReadableMath } from "./markdown-math-core.js";

const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: false,
  breaks: false
});

installMathRules(markdown);
installDefinitionListRule(markdown);
installHighlightRule(markdown);
installFootnoteReferenceRule(markdown);
installInlineFootnoteRule(markdown);

markdown.core.ruler.after("inline", "task_markers", state => {
  let listItemDepth = 0;
  const transformedAtDepth = [];
  for (const token of state.tokens) {
    if (token.type === "list_item_open") {
      listItemDepth += 1;
      transformedAtDepth[listItemDepth] = false;
      continue;
    }
    if (token.type === "list_item_close") {
      transformedAtDepth[listItemDepth] = false;
      listItemDepth -= 1;
      continue;
    }
    if (!listItemDepth || transformedAtDepth[listItemDepth] || token.type !== "inline") continue;
    const first = token.children?.find(child => child.type === "text");
    const match = /^\[([ xX])\]\s+/.exec(first?.content || "");
    if (!match) continue;
    first.content = `${match[1] === " " ? "TOOLBOX_TASK_OPEN" : "TOOLBOX_TASK_DONE"} ${first.content.slice(match[0].length)}`;
    transformedAtDepth[listItemDepth] = true;
  }
});

export function parseMarkdownDocument(source) {
  const extracted = extractMarkdownMetadataAndFootnotes(String(source ?? ""));
  const environment = {
    footnoteNumbers: new Map(extracted.footnotes.map((footnote, index) => [footnote.label, index + 1])),
    inlineFootnotes: [],
    formulaWarnings: new Set()
  };
  const parsed = parseHtmlDocument(markdown.render(extracted.source, environment));
  for (const block of parsed.blocks) {
    if (block.type !== "list-item" || !block.runs?.length) continue;
    const match = /^(TOOLBOX_TASK_OPEN|TOOLBOX_TASK_DONE)\s*/u.exec(block.runs[0].text);
    if (!match) continue;
    block.marker = match[1] === "TOOLBOX_TASK_OPEN" ? "task-open" : "task-done";
    block.runs[0].text = block.runs[0].text.slice(match[0].length);
    block.runs = block.runs.filter(run => run.text);
  }
  const footnotes = [...extracted.footnotes, ...environment.inlineFootnotes];
  if (footnotes.length) appendFootnotes(parsed, footnotes, environment);
  if (extracted.frontMatterRemoved) parsed.diagnostics.push("YAML front matter was treated as document metadata and omitted.");
  if (environment.formulaWarnings.size) {
    parsed.diagnostics.push(`Some formula commands were kept as text: ${[...environment.formulaWarnings].slice(0, 6).join(", ")}.`);
  }
  parsed.diagnostics = [...new Set(parsed.diagnostics)];
  parsed.textLength = parsed.blocks.reduce((total, block) => total + blockTextLength(block), 0);
  return parsed;
}

function installMathRules(instance) {
  instance.block.ruler.before("fence", "toolbox_math_block", (state, startLine, endLine, silent) => {
    const start = state.bMarks[startLine] + state.tShift[startLine];
    const maximum = state.eMarks[startLine];
    const line = state.src.slice(start, maximum).trim();
    const delimiter = line.startsWith("$$") ? "$$" : line.startsWith("\\[") ? "\\[" : "";
    if (!delimiter) return false;
    const close = delimiter === "$$" ? "$$" : "\\]";
    const openingContent = line.slice(delimiter.length);
    const singleLineEnd = openingContent.endsWith(close) ? openingContent.length - close.length : -1;
    if (singleLineEnd >= 0) {
      if (silent) return true;
      const token = state.push("toolbox_math_block", "", 0);
      token.block = true;
      token.content = openingContent.slice(0, singleLineEnd).trim();
      token.map = [startLine, startLine + 1];
      state.line = startLine + 1;
      return true;
    }

    let nextLine = startLine + 1;
    const content = openingContent ? [openingContent] : [];
    for (; nextLine < endLine; nextLine += 1) {
      const lineStart = state.bMarks[nextLine] + state.tShift[nextLine];
      const next = state.src.slice(lineStart, state.eMarks[nextLine]);
      const trimmed = next.trim();
      if (trimmed.endsWith(close)) {
        const beforeClose = trimmed.slice(0, -close.length);
        if (beforeClose) content.push(beforeClose);
        break;
      }
      content.push(next);
    }
    if (nextLine >= endLine) return false;
    if (silent) return true;
    const token = state.push("toolbox_math_block", "", 0);
    token.block = true;
    token.content = content.join("\n").trim();
    token.map = [startLine, nextLine + 1];
    state.line = nextLine + 1;
    return true;
  });

  instance.inline.ruler.before("escape", "toolbox_math_inline", (state, silent) => {
    const start = state.pos;
    const source = state.src;
    let open = "";
    let close = "";
    if (source.startsWith("\\(", start)) {
      open = "\\(";
      close = "\\)";
    } else if (source[start] === "$" && source[start + 1] !== "$") {
      open = "$";
      close = "$";
    } else return false;

    const contentStart = start + open.length;
    if (!source[contentStart] || /\s/u.test(source[contentStart])) return false;
    let end = contentStart;
    while ((end = source.indexOf(close, end)) >= 0) {
      if (source[end - 1] === "\\") {
        end += close.length;
        continue;
      }
      if (end === contentStart || /\s/u.test(source[end - 1])) {
        end += close.length;
        continue;
      }
      if (open === "$" && /\d/u.test(source[start - 1] || "") && /\d/u.test(source[end + 1] || "")) return false;
      if (!silent) {
        const token = state.push("toolbox_math_inline", "", 0);
        token.content = source.slice(contentStart, end);
      }
      state.pos = end + close.length;
      return true;
    }
    return false;
  });

  instance.renderer.rules.toolbox_math_block = (tokens, index, _options, environment) => {
    const rendered = renderFormula(tokens[index].content, environment);
    return `<div data-toolbox-math="block">${instance.utils.escapeHtml(rendered)}</div>\n`;
  };
  instance.renderer.rules.toolbox_math_inline = (tokens, index, _options, environment) => {
    const rendered = renderFormula(tokens[index].content, environment);
    return `<span data-toolbox-math="inline">${instance.utils.escapeHtml(rendered)}</span>`;
  };
}

function installDefinitionListRule(instance) {
  instance.block.ruler.before("paragraph", "toolbox_definition_list", (state, startLine, endLine, silent) => {
    if (startLine + 1 >= endLine) return false;
    const term = state.src.slice(state.bMarks[startLine] + state.tShift[startLine], state.eMarks[startLine]).trim();
    const firstDefinition = lineDefinition(state, startLine + 1);
    if (!term || firstDefinition === null) return false;
    if (silent) return true;

    const termToken = state.push("toolbox_definition_term", "", 0);
    termToken.block = true;
    termToken.content = term;
    termToken.map = [startLine, startLine + 1];
    let line = startLine + 1;
    while (line < endLine) {
      const definition = lineDefinition(state, line);
      if (definition === null) break;
      const token = state.push("toolbox_definition_description", "", 0);
      token.block = true;
      token.content = definition;
      token.map = [line, line + 1];
      line += 1;
    }
    state.line = line;
    return true;
  });
  instance.renderer.rules.toolbox_definition_term = (tokens, index, _options, environment) =>
    `<div data-toolbox-definition="term">${instance.renderInline(tokens[index].content, environment)}</div>\n`;
  instance.renderer.rules.toolbox_definition_description = (tokens, index, _options, environment) =>
    `<div data-toolbox-definition="description">${instance.renderInline(tokens[index].content, environment)}</div>\n`;
}

function installHighlightRule(instance) {
  instance.inline.ruler.before("emphasis", "toolbox_highlight", (state, silent) => {
    if (!state.src.startsWith("==", state.pos)) return false;
    const start = state.pos + 2;
    const end = state.src.indexOf("==", start);
    if (end <= start || /\s/u.test(state.src[start]) || /\s/u.test(state.src[end - 1])) return false;
    if (!silent) {
      const open = state.push("mark_open", "mark", 1);
      open.markup = "==";
      const text = state.push("text", "", 0);
      text.content = state.src.slice(start, end);
      const close = state.push("mark_close", "mark", -1);
      close.markup = "==";
    }
    state.pos = end + 2;
    return true;
  });
}

function installFootnoteReferenceRule(instance) {
  instance.inline.ruler.before("link", "toolbox_footnote_reference", (state, silent) => {
    if (!state.src.startsWith("[^", state.pos)) return false;
    const end = state.src.indexOf("]", state.pos + 2);
    if (end < 0) return false;
    const label = state.src.slice(state.pos + 2, end);
    const number = state.env?.footnoteNumbers?.get(label);
    if (!number) return false;
    if (!silent) {
      const token = state.push("toolbox_footnote_reference", "", 0);
      token.content = String(number);
    }
    state.pos = end + 1;
    return true;
  });
  instance.renderer.rules.toolbox_footnote_reference = (tokens, index) =>
    `<sup data-toolbox-footnote="reference">${tokens[index].content}</sup>`;
}

function installInlineFootnoteRule(instance) {
  instance.inline.ruler.before("toolbox_footnote_reference", "toolbox_inline_footnote", (state, silent) => {
    if (!state.src.startsWith("^[", state.pos)) return false;
    let end = state.pos + 2;
    while ((end = state.src.indexOf("]", end)) >= 0 && state.src[end - 1] === "\\") end += 1;
    if (end < 0 || end === state.pos + 2) return false;
    if (!silent) {
      const number = (state.env?.footnoteNumbers?.size || 0) + (state.env?.inlineFootnotes?.length || 0) + 1;
      state.env?.inlineFootnotes?.push({ label: `inline-${number}`, content: state.src.slice(state.pos + 2, end) });
      const token = state.push("toolbox_footnote_reference", "", 0);
      token.content = String(number);
    }
    state.pos = end + 1;
    return true;
  });
}

function renderFormula(source, environment) {
  const result = latexToReadableMath(source);
  for (const command of result.unsupportedCommands) environment?.formulaWarnings?.add(command);
  return result.text;
}

function lineDefinition(state, line) {
  const source = state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line]);
  const match = /^:\s+(.+)$/u.exec(source);
  return match ? match[1].trim() : null;
}

function extractMarkdownMetadataAndFootnotes(source) {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  let frontMatterRemoved = false;
  let start = 0;
  if (lines[0]?.trim() === "---") {
    const closing = lines.slice(1).findIndex(line => line.trim() === "---");
    const metadataLines = closing >= 0 ? lines.slice(1, closing + 1) : [];
    if (closing >= 0 && metadataLines.some(line => /^[A-Za-z_][\w-]*\s*:/u.test(line))) {
      start = closing + 2;
      frontMatterRemoved = true;
    }
  }

  const output = [];
  const footnotes = [];
  let fence = "";
  for (let index = start; index < lines.length; index += 1) {
    const line = lines[index];
    const fenceMatch = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
    if (fenceMatch) {
      const marker = fenceMatch[1][0];
      fence = fence === marker ? "" : fence || marker;
      output.push(line);
      continue;
    }
    const match = !fence && /^\s{0,3}\[\^([^\]]+)\]:\s*(.*)$/u.exec(line);
    if (!match) {
      output.push(line);
      continue;
    }
    const content = [match[2]];
    while (index + 1 < lines.length) {
      const next = lines[index + 1];
      if (/^(?: {4}|\t)/.test(next)) {
        content.push(next.replace(/^(?: {4}|\t)/, ""));
        index += 1;
        continue;
      }
      if (!next.trim() && /^(?: {4}|\t)/.test(lines[index + 2] || "")) {
        content.push("");
        index += 1;
        continue;
      }
      break;
    }
    footnotes.push({ label: match[1], content: content.join("\n").trim() });
  }
  return { source: output.join("\n"), footnotes, frontMatterRemoved };
}

function appendFootnotes(parsed, footnotes, environment) {
  parsed.blocks.push({ type: "rule" });
  parsed.blocks.push({ type: "heading", level: 2, runs: [{ text: "Footnotes" }] });
  footnotes.forEach((footnote, index) => {
    const fragment = parseHtmlDocument(markdown.render(footnote.content, environment));
    const runs = fragment.blocks.flatMap((block, blockIndex) => [
      ...(blockIndex ? [{ text: "\n" }] : []),
      ...(footnoteBlockRuns(block))
    ]);
    parsed.blocks.push({
      type: "list-item",
      marker: `${index + 1}.`,
      depth: 0,
      runs: runs.length ? runs : [{ text: footnote.content }]
    });
    parsed.diagnostics.push(...fragment.diagnostics);
  });
}

function footnoteBlockRuns(block) {
  if (block.runs) {
    const prefix = block.type === "list-item" ? [{ text: `${block.marker || "•"} ` }] : [];
    return [...prefix, ...block.runs];
  }
  if (block.rows) return [{ text: block.rows.flatMap(row => row.cells).join(" ") }];
  return [];
}

function blockTextLength(block) {
  if (block.rows) return block.rows.flatMap(row => row.cells).join("").length;
  return (block.runs || []).reduce((total, run) => total + String(run.text || "").length, 0);
}
